<template>
  <ul :data-level="level" class="navbar-nav me-auto mb-2 mb-lg-0 ms-3">
    <li v-for="route in nodes" :key="route.name" class="nav-item d-flex">
      <!-- Ha meg van adva, egy egyedi komponens jelenik meg az elem előtt -->
      <component v-if="route.meta.componentBefore" :is="route.meta.componentBefore" />
      <router-link
        class="nav-link d-flex align-items-center"
        :class="{
          'group-active': isRouteGroupActive(route),
        }"
        :to="getNodeToValue(route)"
        @click="$emit('navigation')"
        exact-active-class="active"
      >
        <!-- SVG ikon beszúrása az elem elé, ha van megadva ilyen ikon -->
        <SvgIcon v-if="route.meta.iconBefore" :src="route.meta.iconBefore" class="me-2" />
        <span>{{ route.meta.translate ? $t(route.meta.label) : route.meta.label }}</span>
        <SvgIcon v-if="route.meta.iconAfter" :src="route.meta.iconAfter" class="ms-2" />
        <!-- SVG ikon beszúrása az elem után, ha van megadva ilyen ikon -->
      </router-link>
      <NavigationNode
        v-if="route.children"
        :routes="route.children"
        :level="level + 1"
        exact-active-class="active"
      />
      <!-- Ha meg van adva, egy egyedi komponens jelenik meg az elem után -->
      <component v-if="route.meta.componentAfter" :is="route.meta.componentAfter" />
    </li>
  </ul>
</template>

<script>
import SvgIcon from "./SvgIcon.vue";

export default {
  components: { SvgIcon },
  emits: ["navigation"],

  name: "NavigationNode",

  props: {
    routes: {
      required: true,
      type: Array,
    },

    level: {
      required: false,
      type: Number,
      default: 0,
    },
  },

  methods: {
    // Szülő menüpont vizuális jelölése, ha annak valamelyik kapcsolódó almenüje aktív.
    //
    // Fontos:
    // ezek az oldalak route szinten nem valódi children elemek,
    // hanem külön route-ok, ezért a Vue Router alap active állapota
    // nem jelöli automatikusan aktívnak a logikai szülő menüpontot.
    //
    // Paraméter-kezelés csoport:
    //
    // - jifadmin.parametermanagement
    // - jifadmin.emailmasterdata
    // - jifadmin.emailnotificationrules
    // - jifadmin.selecttextmanagement
    //
    // Felhasználó-kezelés csoport:
    //
    // - jifadmin.usermanagement
    // - jifadmin.userldapsearch
    // - jifadmin.userdatabasesearch
    //
    // Például amikor a felhasználó az Email törzsadatok,
    // Választható szövegek vagy Email értesítések oldalon van,
    // akkor a Paraméter-kezelés menüpont kapjon vizuális jelölést.
    //
    // Ugyanígy amikor LDAP felhasználókeresés vagy
    // Helyi felhasználók kezelése oldalon van,
    // akkor a Felhasználó-kezelés menüpont kapjon jelölést.
    //
    // A "group-active" class csak finom keretet ad a szülő menünek,
    // nem ugyanaz, mint az "active" fekete kijelölés.
    isRouteGroupActive(route) {
      if (route.name === "jifadmin.parametermanagement") {
        return [
          "jifadmin.emailmasterdata",
          "jifadmin.emailnotificationrules",
          "jifadmin.selecttextmanagement",
        ].includes(this.$route.name);
      }

      if (route.name === "jifadmin.usermanagement") {
        return ["jifadmin.userldapsearch", "jifadmin.userdatabasesearch"].includes(
          this.$route.name
        );
      }

      return false;
    },

    checkAndRedirect() {
      if (this.$route.name !== "dashboard") return;

      const roles = this.$user?.data?.roles ?? [];

      let targetRoute = null;

      if (roles.includes("Adminisztrátor")) {
        targetRoute = "dashboard";
      } else if (roles.includes("JIF admin")) {
        targetRoute = "jifadmin.worklist";
      } else if (roles.includes("Főigazgató")) {
        targetRoute = "director.worklist";
      } else if (roles.includes("JIF szakértő")) {
        targetRoute = "expert.worklist";
      } else if (roles.includes("Ellenőr")) {
        targetRoute = "reviewer.worklist";
      }

      if (targetRoute) {
        this.$router.replace({ name: targetRoute });
      }
    },

    filterHiddenRoutes(routes) {
      const nodes = _.filter(routes, (route) => {
        if (!this.$user?.data) {
          /*
           * console.log("Nincs bejelentkezve.");
           */
          return false;
        }

        if (!this.$user?.data?.permissions || !this.$user?.data?.roles) {
          /*
           * console.error("Hiányzó felhasználói jogosultságok vagy szerepkörök");
           */
          return false;
        }
        /*
         * Általános információ a felhasználó jogosultságairól és szerepköreiről.
         * console.info("Felhasználó jogosultságai:", this.$user.data.permissions);
         * console.info("Felhasználó szerepkörei:", this.$user.data.roles);
         */
        if (
          typeof route.meta.hidden === "function"
            ? route.meta.hidden(this.$root)
            : route.meta.hidden
        ) {
          /*
           * console.info("Az útvonal rejtett:", route.name);
           */
          return false;
        }

        let options = {
          route: {
            to: this.$router.resolve(route),
          },
        };

        if (route.meta.permissions?.length) {
          const hasPermission = route.meta.permissions.some((permission) =>
            this.$user.data.permissions.includes(permission)
          );
          if (!hasPermission) {
            /*
             * console.warn("Nincs jogosultság a következő útvonalhoz:", route.name);
             */
            return false;
          }
        }

        if (route.meta.roles?.length && !this.$user.hasRole(route.meta.roles, options)) {
          /*
           * console.warn("Nincs szerepkör a következő útvonalhoz:", route.name);
           */
          return false;
        }

        /*
         * console.info("Az útvonal szerepel a menüben:", route.name);
         */
        return true;
      });

      return _.orderBy(nodes, [(node) => node.meta?.priority ?? 0], ["desc"]);
    },

    getNodeToValue(node) {
      let obj = {};

      obj[node.name ? "name" : "path"] = node.name ?? node.path;

      return obj;
    },
  },

  computed: {
    nodes() {
      this.checkAndRedirect();
      return this.filterHiddenRoutes(this.routes);
    },
  },
};
</script>

<style scoped>
.nav-link.group-active {
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.025);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}
</style>
